import { AttributeValueInput } from "../types";
import { MappingContext } from "../orchestrator";
import { withScope, captureException } from "@sentry/react";
import { memoizeMapperFactory } from "../utils/memoize";
import { z } from "zod";
import {
  medicationValueSchema,
  medicationGroupValueSchema
} from "../schemas";

/**
 * Schema for multiSelectMapper configuration.
 * Maps individual values to their attribute mappings, with special handling for "none" options.
 */
const multiSelectMapperConfigSchema = z.object({
  // Map of individual values to their attributes
  valueMap: z.record(
    z.string(),
    z.array(
      z.object({
        attributeDefinitionId: z.number(),
        booleanAttributeValue: z.boolean().optional(),
        stringAttributeValue: z.string().optional(),
        integerAttributeValue: z.number().optional(),
        floatAttributeValue: z.number().optional(),
        medicationAttributeValue: medicationValueSchema.optional(),
        medicationGroupAttributeValue: medicationGroupValueSchema.optional(),
      })
    )
  ),
  // Special handling for "none" options
  noneValue: z.string().optional(),
  // When "none" is selected, what attributes to set
  noneAttributeMap: z.record(
    z.string(),
    z.array(
      z.object({
        attributeDefinitionId: z.number(),
        booleanAttributeValue: z.boolean().optional(),
        stringAttributeValue: z.string().optional(),
        integerAttributeValue: z.number().optional(),
        floatAttributeValue: z.number().optional(),
        medicationAttributeValue: medicationValueSchema.optional(),
        medicationGroupAttributeValue: medicationGroupValueSchema.optional(),
      })
    )
  ).optional(),
});

// Type is inferred from schema, no need to declare separately

/**
 * Internal implementation of multiSelectMapper without memoization.
 */
function multiSelectMapperImpl(config: unknown) {
  const parsedConfig = multiSelectMapperConfigSchema.parse(config);

  return (response: string | string[], _context: MappingContext): AttributeValueInput[] => {
    try {
      // Ensure response is an array - handle both string and array inputs
      const selections = Array.isArray(response) ? response : (response ? [response] : []);
      const attributes: AttributeValueInput[] = [];
      const processedAttributeIds = new Set<number>();

      // Handle "none of the above" logic
      if (parsedConfig.noneValue && selections.includes(parsedConfig.noneValue)) {
        // If "none" is selected, use the none attribute mappings
        if (parsedConfig.noneAttributeMap) {
          Object.values(parsedConfig.noneAttributeMap).forEach(mappings => {
            mappings.forEach(attr => {
              // Avoid duplicate attributes
              if (!processedAttributeIds.has(attr.attributeDefinitionId)) {
                attributes.push({ ...attr, isSkipped: false });
                processedAttributeIds.add(attr.attributeDefinitionId);
              }
            });
          });
        }
      } else {
        // Process each selected value
        selections.forEach(value => {
          const mapping = parsedConfig.valueMap[value];
          if (mapping) {
            mapping.forEach(attr => {
              // Avoid duplicate attributes
              if (!processedAttributeIds.has(attr.attributeDefinitionId)) {
                attributes.push({ ...attr, isSkipped: false });
                processedAttributeIds.add(attr.attributeDefinitionId);
              }
            });
          }
        });

        // Set false for unselected boolean options (only if not "none")
        Object.entries(parsedConfig.valueMap).forEach(([value, mappings]) => {
          if (!selections.includes(value) && value !== parsedConfig.noneValue) {
            mappings.forEach(mapping => {
              // Only set false for boolean attributes that haven't been processed
              if ('booleanAttributeValue' in mapping && 
                  mapping.booleanAttributeValue === true &&
                  !processedAttributeIds.has(mapping.attributeDefinitionId)) {
                attributes.push({
                  attributeDefinitionId: mapping.attributeDefinitionId,
                  booleanAttributeValue: false,
                  isSkipped: false
                });
                processedAttributeIds.add(mapping.attributeDefinitionId);
              }
            });
          }
        });
      }

      return attributes;
    } catch (e) {
      console.error("Failed to process multi-select response:", response, e);

      withScope((scope) => {
        scope.setTag("location", "multiSelectMapper");
        scope.setContext("mapperContext", {
          response,
          configKeys: Object.keys(parsedConfig.valueMap),
        });
        captureException(e);
      });

      return [];
    }
  };
}

/**
 * Creates a mapper for multi-select checkbox questions.
 * It handles arrays of selected values and maps them to attributes.
 * 
 * @param config - Configuration object with:
 *   - valueMap: Map of values to their attribute mappings
 *   - noneValue: Optional value that represents "none of the above"
 *   - noneAttributeMap: Attributes to set when "none" is selected
 * 
 * @example
 * ```typescript
 * multiSelectMapper({
 *   valueMap: {
 *     hypertension: [{ attributeDefinitionId: 123, booleanAttributeValue: true }],
 *     diabetes: [{ attributeDefinitionId: 456, booleanAttributeValue: true }],
 *   },
 *   noneValue: "none",
 *   noneAttributeMap: {
 *     none: [
 *       { attributeDefinitionId: 123, booleanAttributeValue: false },
 *       { attributeDefinitionId: 456, booleanAttributeValue: false },
 *     ]
 *   }
 * })
 * ```
 * 
 * This function is memoized to avoid repeated config parsing.
 */
export const multiSelectMapper = memoizeMapperFactory(multiSelectMapperImpl);